import React, { useContext, useEffect, useMemo, useCallback, useState } from 'react';
import { IGetRestaurantsShort, IGetRestaurantsShortArgs, EntityStatus } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { DevicesContext, IDevicesContext } from 'modules/Devices/Devices/devicesState/devicesContext';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';

export const RestaurantSelect: React.FC = () => {
	const { setIDRestaurant, IDSelectedCustomer, IDSelectedRestaurant } = useContext<IDevicesContext>(DevicesContext);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		IDSelectedCustomer ? getRestaurantsShortInfo : null,
		{
			customerId: IDSelectedCustomer || '',
		},
	);
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);

	useEffect(() => {
		refetch();
	}, [IDSelectedCustomer]);

	const restaurants = useMemo(() => {
		const items = response?.data.restaurants;
		const visibleItems = showArchived
			? items
			: items?.filter(
					(restaurant) => restaurant.status === EntityStatus.Active || restaurant.id === IDSelectedRestaurant,
			  );

		return visibleItems?.map((restaurant) => ({
			...restaurant,
			additionalContent: restaurant.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [response, showArchived, IDSelectedRestaurant]);

	const value = IDSelectedCustomer && !loading ? IDSelectedRestaurant : undefined;

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	return (
		<OutsideFormSelect
			containerClassName="!min-h-min w-px"
			className="!mt-0"
			testId="RestaurantSelect"
			name="RestaurantSelect"
			label="restaurantLabel"
			onChange={setIDRestaurant}
			onRefetch={refetch}
			value={value}
			options={restaurants}
			listItemHeight={restaurants?.length}
			disabled={!IDSelectedCustomer || loading}
			loading={loading}
			fetchError={responseError}
			withValidation={false}
			keepSearchWithDropdownRender={true}
			dropdownRender={renderSelectMenu}
		/>
	);
};
